import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Trash2, Eye, Upload, FileText, X, Calendar, Download, Plus, FolderOpen
} from 'lucide-react';

// ✅ FIXED: matches your main urls.py (members under /api/members/)
const API_BASE_URL = 'https://backend.dlapakistan.com/api/members';

const PdfUploaderPage = () => {
  const [pdfs, setPdfs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [pdfName, setPdfName] = useState('');
  const [pdfCategory, setPdfCategory] = useState('DRAP');
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeTab, setActiveTab] = useState('ALL');
  const fileInputRef = useRef(null);

  const getAuthToken = () => localStorage.getItem('access_token');

  const fetchPdfs = async (category = null) => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        console.warn('No auth token found');
        setIsLoading(false);
        return;
      }
      let url = `${API_BASE_URL}/pdfs/`;
      if (category && category !== 'ALL') {
        url += `?category=${category}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPdfs(response.data);
    } catch (error) {
      console.error('Failed to fetch PDFs', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPdfs(activeTab);
  }, [activeTab]);

  const openUploadModal = () => {
    setPdfName('');
    setPdfCategory('DRAP');
    setSelectedFile(null);
    setIsUploadModalOpen(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      const nameWithoutExt = file.name.replace(/\.pdf$/i, '');
      setPdfName(nameWithoutExt);
    } else {
      alert('Please select a valid PDF file');
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      alert('Please select a PDF file');
      return;
    }
    if (!pdfName.trim()) {
      alert('Please enter a name for the PDF');
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('name', pdfName.trim());
    formData.append('category', pdfCategory);
    formData.append('file', selectedFile);

    try {
      const token = getAuthToken();
      const response = await axios.post(`${API_BASE_URL}/pdfs/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      setPdfs(prev => [response.data, ...prev]);
      setIsUploadModalOpen(false);
      setSelectedFile(null);
      setPdfName('');
      setPdfCategory('DRAP');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Upload failed', error);
      alert('Upload failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this PDF?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_BASE_URL}/pdfs/${id}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPdfs(prev => prev.filter(pdf => pdf.id !== id));
    } catch (error) {
      console.error('Delete failed', error);
      alert('Delete failed');
    }
  };

  const handleView = async (pdf) => {
    try {
      const token = getAuthToken();
      if (!token) {
        alert('Authentication token missing');
        return;
      }
      const response = await axios.get(`${API_BASE_URL}/pdfs/${pdf.id}/download/`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      setSelectedPdf({ ...pdf, blobUrl, blob });
    } catch (error) {
      console.error('Failed to load PDF', error);
      alert('Could not load PDF. Please try again.');
    }
  };

  const handleDownload = async (pdf) => {
    try {
      const token = getAuthToken();
      if (!token) {
        alert('Authentication token missing');
        return;
      }
      const response = await axios.get(`${API_BASE_URL}/pdfs/${pdf.id}/download/`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${pdf.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed', error);
      alert('Download failed. Please try again.');
    }
  };

  const closeViewer = () => {
    if (selectedPdf && selectedPdf.blobUrl) {
      URL.revokeObjectURL(selectedPdf.blobUrl);
    }
    setSelectedPdf(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getInitials = (name) => name.charAt(0).toUpperCase();

  const getCategoryColor = (category) => {
    switch(category) {
      case 'DRAP':
        return 'bg-purple-100 text-purple-700';
      case 'HEALTHCARE':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryBadge = (category, categoryDisplay) => {
    const colorClass = getCategoryColor(category);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {categoryDisplay || category}
      </span>
    );
  };

  const renderEmptyState = () => (
    <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
        <FileText className="w-8 h-8 text-emerald-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">No PDFs uploaded yet</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        Upload PDF documents and give them a custom name and category.
      </p>
      <button
        onClick={openUploadModal}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg"
      >
        <Upload className="w-4 h-4" />
        Upload PDF
      </button>
    </div>
  );

  const renderTabs = () => (
    <div className="flex gap-2 mb-6 border-b border-gray-200">
      <button
        onClick={() => setActiveTab('ALL')}
        className={`px-6 py-3 text-sm font-medium transition-all duration-200 relative ${
          activeTab === 'ALL'
            ? 'text-emerald-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <div className="flex items-center gap-2">
          <FolderOpen className="w-4 h-4" />
          All PDFs
        </div>
        {activeTab === 'ALL' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-full"></div>
        )}
      </button>
      <button
        onClick={() => setActiveTab('DRAP')}
        className={`px-6 py-3 text-sm font-medium transition-all duration-200 relative ${
          activeTab === 'DRAP'
            ? 'text-emerald-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
          DRAP
        </div>
        {activeTab === 'DRAP' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-full"></div>
        )}
      </button>
      <button
        onClick={() => setActiveTab('HEALTHCARE')}
        className={`px-6 py-3 text-sm font-medium transition-all duration-200 relative ${
          activeTab === 'HEALTHCARE'
            ? 'text-emerald-600'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          Healthcare
        </div>
        {activeTab === 'HEALTHCARE' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-full"></div>
        )}
      </button>
    </div>
  );

  const renderGrid = () => (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            {activeTab === 'ALL' ? 'All Documents' : activeTab === 'DRAP' ? 'DRAP Documents' : 'Healthcare Documents'}
          </h2>
          <p className="text-sm text-gray-500">{pdfs.length} document(s)</p>
        </div>
        <button
          onClick={openUploadModal}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all duration-200 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add PDF
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {pdfs.map((pdf) => (
          <div key={pdf.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden hover:border-emerald-200">
            <div className="p-5">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  {getCategoryBadge(pdf.category, pdf.category_display)}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleView(pdf)} className="p-2 bg-gray-50 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-all duration-200" title="View PDF">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDownload(pdf)} className="p-2 bg-gray-50 rounded-lg hover:bg-blue-50 text-blue-600 transition-all duration-200" title="Download">
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(pdf.id)} className="p-2 bg-gray-50 rounded-lg hover:bg-red-50 text-red-500 transition-all duration-200" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-3 shadow-md ${
                  pdf.category === 'DRAP' 
                    ? 'bg-gradient-to-br from-purple-400 to-purple-600'
                    : 'bg-gradient-to-br from-blue-400 to-blue-600'
                }`}>
                  <span className="text-white text-2xl font-bold">{getInitials(pdf.name)}</span>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-1 line-clamp-2">{pdf.name}</h3>
                <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-2">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{formatDate(pdf.uploaded_at)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="flex-1">
      <div className="sticky top-14 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-8 py-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">PDF Uploader</h1>
            <p className="text-gray-500 text-sm mt-1">Upload, view, download, and manage your PDF documents</p>
          </div>
        </div>
      </div>
      <div className="px-8 py-6 mt-16">
        {renderTabs()}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
            <p className="ml-3 text-gray-500">Processing...</p>
          </div>
        ) : pdfs.length === 0 ? (
          renderEmptyState()
        ) : (
          renderGrid()
        )}
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.target === e.currentTarget && setIsUploadModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-800">Upload PDF</h3>
              <button onClick={() => setIsUploadModalOpen(false)} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={pdfName}
                  onChange={(e) => setPdfName(e.target.value)}
                  placeholder="Enter a name for this PDF"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
                <p className="text-xs text-gray-400 mt-1">Used for display and download filename.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={pdfCategory}
                  onChange={(e) => setPdfCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="DRAP">DRAP</option>
                  <option value="HEALTHCARE">Healthcare</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Select the category for this PDF document.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PDF File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                />
                {selectedFile && <p className="text-xs text-gray-500 mt-1">Selected: {selectedFile.name}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200 bg-gray-50">
              <button onClick={() => setIsUploadModalOpen(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors">Cancel</button>
              <button onClick={handleUploadSubmit} className="px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {selectedPdf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={closeViewer}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <h3 className="font-semibold text-gray-800 truncate">{selectedPdf.name}</h3>
                {selectedPdf.category && (
                  <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    selectedPdf.category === 'DRAP' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {selectedPdf.category_display || selectedPdf.category}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleDownload(selectedPdf)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Download">
                  <Download className="w-5 h-5 text-gray-600" />
                </button>
                <button onClick={closeViewer} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              <iframe
                src={selectedPdf.blobUrl}
                title={selectedPdf.name}
                className="w-full h-full"
                style={{ border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}
      <style>{`.line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }`}</style>
    </div>
  );
};

export default PdfUploaderPage;